import React, { forwardRef, useState, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { testImage, stringToHslColor, gradientBackground } from '@evertel/utils'
import { useService } from '@evertel/di'
import { UserController } from '@evertel/blue-user'

interface ProfilePicProps {
    userId: number // if you only pass in userId, the userController will fetch the user. If you have the raw user data, pass in the rest of the props
    className?: string

    /* you CAN pass in direct data IF you are using raw data i.e. in a search list.
       otherwise, just pass in the userId */
    imageUrl?: string
    firstName?: string
    lastName?: string

    isGuest?: boolean
    width?: number
    height?: number
    style?: React.CSSProperties
    title?: boolean
}

const ProfilePic = observer(forwardRef<HTMLImageElement, ProfilePicProps>(({
    userId,
    imageUrl,
    firstName,
    lastName,
    isGuest,
    width = 40,
    height = 40,
    title = false,
    className,
    ...otherProps
}, ref) => {

    const userController = useService(UserController, [])

    const [isImageValid, setIsImageValid] = useState(true)

    const useRawData = firstName || lastName || imageUrl
    const profileImage = imageUrl || userController.user?.publicImage

    useEffect(() => {
        // only init the user controller if only the userId is passed in
        if (!useRawData && userId) {
            userController.init(userId)
        }
    }, [userId])

    useEffect(() => {
        // make sure image exists on server, if not, we render initials block
        if (profileImage) {
            testImage(profileImage).then(result => {
                if (result === 'error') {
                    setIsImageValid(false)
                }
            })
                .catch((error) => {
                    setIsImageValid(false)
                    //console.log('ProfilePic Error', {imageUrl})
                })
        }
    }, [profileImage])

    const [fullName, shortName] = useMemo(() => {
        const displayFirstName = firstName || userController.user?.firstName || '?'
        const displayLastName = lastName || userController.user?.lastName || '?'

        return [
            `${displayFirstName} ${displayLastName}`,
            `${displayFirstName.slice(0, 1)}${displayLastName.slice(0, 1)}`
        ]
    }, [firstName, lastName, userController.user])

    const backgroundStyle = useMemo(() => {
        if (!fullName.trim() || fullName === '? ?') {
            return { backgroundColor: stringToHslColor(String(userId)) }
        }
        return { background: gradientBackground(fullName) }
    }, [fullName, userId])

    const _classNames = classNames(
        'profile-pic text-uppercase',
        {
            'no-avatar': !profileImage || !isImageValid
        },
        className
    )

    const renderInfo = () => {
        if (profileImage && isImageValid) {
            // valid image
            return (
                <img
                    ref={ref}
                    src={profileImage}
                    className={_classNames}
                    alt={fullName}
                    width={width}
                    height={height}
                    title={title ? fullName : undefined}
                    {...otherProps}
                />
            )
        } else {
            // invalid image or no image
            const { style, ...rest } = otherProps

            const fontSize = Math.max(12, Math.floor(width / 3))

            return (
                <div
                    ref={ref}
                    className={classNames(_classNames)}
                    style={{
                        ...style,
                        ...backgroundStyle,
                        width,
                        height,
                        fontSize
                    }}
                    {...rest}
                    title={title ? fullName : undefined}
                >
                    {shortName}
                </div>
            )
        }
    }

    return (
        <div style={{
            position: 'relative'
        }}>
            {renderInfo()}
            {(isGuest) &&
                <div className="profile-pic-badge">
                    G
                </div>
            }
        </div>
    )
}))

export { ProfilePic }
